import type { GridColDef } from '@mui/x-data-grid';
import type { ContractsReport } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined, type ItemOf, type ValueOf } from '@weft/utils';
import { ContractStatusChip } from 'apps/app/src/components/design/ContractStatusChip';
import { getFormattedNumberWithCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import { compareDatesDescending, formatDate } from 'apps/app/src/utils/date';
import { formatNumberWithPrecision } from 'apps/app/src/utils/number';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';

export const CONTRACTS_REPORT_KEYS = Object.freeze({
  CONTRACT_NAME: 'contractName',
  CUSTOMER_NAME: 'customerName',
  AUTO_RENEWAL: 'autoRenewal',
  DISCOUNT_AMOUNT: 'discountAmount',
  OVERAGE_AMOUNT: 'overageAmount',
  START_DATE: 'startDate',
  DAYS_TO_CONTRACT_END_DATE: 'daysToEnd',
  STATUS: 'status',
  TRIAL_PERIOD: 'trialPeriod',
  ESTIMATED_AMOUNT: 'estimatedAmount',
  SALES_ORDER_SYNC: 'syncStatusUpdatedAt',
} satisfies Record<string, keyof ItemOf<ContractsReport>>);

export type ContractsReportColumnKeys = ValueOf<typeof CONTRACTS_REPORT_KEYS>;

export const CONTRACTS_REPORT_COLUMNS: GridColDef<ItemOf<ContractsReport>>[] = [
  {
    field: CONTRACTS_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer name',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => params.row.customerName,
    headerAlign: 'center',
    align: 'left',
    cellClassName: 'pl-8',
    renderCell: ({ row: { customerName } }) => (
      <VyTableCell
        value={customerName}
        containerClassName='justify-start'
      />
    ),
  },
  {
    field: CONTRACTS_REPORT_KEYS.CONTRACT_NAME,
    headerName: 'Contract name',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => params.row.contractName,
    headerAlign: 'center',
    align: 'left',
    cellClassName: 'pl-8',
    renderCell: ({ row: { contractName } }) => (
      <VyTableCell
        value={contractName}
        containerClassName='justify-start'
      />
    ),
  },
  {
    field: CONTRACTS_REPORT_KEYS.STATUS,
    headerName: 'Status',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 250,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { status, endDate } }) => (
      <ContractStatusChip
        status={status}
        endDate={endDate}
      />
    ),
  },
  {
    field: CONTRACTS_REPORT_KEYS.START_DATE,
    headerName: 'Start date',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row }) => formatDate(row.startDate),
    sortComparator: (createdAt1: Date, createdAt2: Date) => compareDatesDescending(createdAt1, createdAt2),
  },
  {
    field: CONTRACTS_REPORT_KEYS.DAYS_TO_CONTRACT_END_DATE,
    headerName: 'Time to end',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { daysToEnd } }) => <VyTableCell value={getDaysToEndLabel(daysToEnd)}/>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.ESTIMATED_AMOUNT,
    headerName: 'Estimation',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: ({ value }) => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { estimatedAmount } }) => <Price>{estimatedAmount}</Price>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.SALES_ORDER_SYNC,
    headerName: 'SO Sync',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { syncStatusUpdatedAt } }) => <VyTableCell value={isNotNullOrUndefined(syncStatusUpdatedAt) ? String(syncStatusUpdatedAt) : '--'}/>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.TRIAL_PERIOD,
    headerName: 'Trial period',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 125,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { trialPeriod } }) => <VyTableCell value={trialPeriod ?? '--'}/>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.OVERAGE_AMOUNT,
    headerName: 'Overage $$',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: ({ value }) => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { currency, overageAmount } }) => <VyTableCell value={getFormattedNumberWithCurrencySymbol(currency, overageAmount)}/>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.DISCOUNT_AMOUNT,
    headerName: 'Discount $$',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: ({ value }) => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { currency, discountAmount } }) => <VyTableCell value={getFormattedNumberWithCurrencySymbol(currency, discountAmount)}/>,
  },
  {
    field: CONTRACTS_REPORT_KEYS.AUTO_RENEWAL,
    headerName: 'Auto renewal',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { autoRenewal } }) => <VyTableCell value={autoRenewal ? 'Yes' : 'No'}/>,
  },
];

function getDaysToEndLabel(daysToEnd: number | undefined): string {
  return isNullOrUndefined(daysToEnd) 
    ? 'Unlimited' 
    : daysToEnd < 0 
      ? 'Expired'
      : `${daysToEnd} days`;
}